/**
 * AgentWindow — the unified Cortex agent workspace.
 *
 * Three-pane layout (Devin-style):
 *   SessionRail | Timeline + Composer | Workbench
 *
 * One engine (useCortexEngine) drives both display modes:
 *   - "workbench" (fullscreen /cortex): all three panes
 *   - "assistant" (drawer): timeline + composer only, no rail/workbench
 */

import { useRef, useState } from "react";
import { Maximize2, Minimize2, X, ArrowUpRight, PanelLeft, PanelRight } from "lucide-react";
import type { ImperativePanelHandle } from "react-resizable-panels";
import { cn } from "@flowstate/utils";
import { Button } from "@/components/ui/button";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { useCurrentOrganization } from "@/hooks/useCurrentOrganization";
import { useCortexEngine } from "../engine/useCortexEngine";
import type { SessionKind } from "../engine/sessionStores";
import { SessionRail } from "./SessionRail";
import { AgentTimeline } from "./AgentTimeline";
import { AgentComposer } from "./AgentComposer";
import { Workbench } from "./Workbench";
import { agentBranding } from "@/features/ai-agent/config/agentBranding";

interface AgentWindowProps {
  mode: "workbench" | "assistant";
  /** Initial session kind (default "chat"). */
  initialKind?: SessionKind;
  onClose?: () => void;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
}

export function AgentWindow({
  mode,
  initialKind = "chat",
  onClose,
  onToggleFullscreen,
  isFullscreen,
}: AgentWindowProps) {
  const { currentOrganization } = useCurrentOrganization();
  const { projects, currentProject } = useCurrentProject();

  const engine = useCortexEngine({ kind: initialKind });

  // Resizable pane handles (rail + workbench) with collapse toggles.
  const railPanelRef = useRef<ImperativePanelHandle>(null);
  const workbenchPanelRef = useRef<ImperativePanelHandle>(null);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [workbenchCollapsed, setWorkbenchCollapsed] = useState(false);

  const pausedLabel = engine.pausedRun
    ? engine.pausedRun.status === "awaiting_input"
      ? "waiting for your answer"
      : `waiting on ${engine.pausedRun.specialistType ?? "specialist"}`
    : null;

  const showPanes = mode === "workbench";

  const center = (
    <div className="flex-1 flex flex-col min-w-0 min-h-0 h-full">
      <AgentTimeline
        messages={engine.messages}
        timeline={engine.timeline}
        runs={engine.runs}
        isLoading={engine.isLoading}
        onAnswer={(runId, opts) => void engine.resumeRun(runId, opts)}
        compact={mode === "assistant"}
      />
      <AgentComposer engine={engine} pausedLabel={pausedLabel} />
    </div>
  );

  return (
    <div className="flex flex-col h-full min-h-0 bg-background/70 backdrop-blur-xl">
      {/* Window header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/30 shrink-0">
        <span className="text-sm font-semibold">{agentBranding.name}</span>
        {engine.sessionId && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted/50 text-muted-foreground">
            {engine.sessionKind === "work" ? "work session" : "chat"}
          </span>
        )}
        {engine.queuedCount > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/15 text-primary">
            {engine.queuedCount} queued
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {showPanes && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className={cn("h-7 w-7 p-0", railCollapsed && "text-muted-foreground")}
                onClick={() =>
                  railCollapsed ? railPanelRef.current?.expand() : railPanelRef.current?.collapse()
                }
                title={railCollapsed ? "Show sessions" : "Hide sessions"}
              >
                <PanelLeft className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={cn("h-7 w-7 p-0", workbenchCollapsed && "text-muted-foreground")}
                onClick={() =>
                  workbenchCollapsed
                    ? workbenchPanelRef.current?.expand()
                    : workbenchPanelRef.current?.collapse()
                }
                title={workbenchCollapsed ? "Show workbench" : "Hide workbench"}
              >
                <PanelRight className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
          {engine.sessionKind === "chat" && engine.sessionId && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[11px] gap-1 text-muted-foreground hover:text-foreground"
              onClick={() => void engine.promoteToWorkSession()}
              title="Promote this chat to a persistent work session"
            >
              <ArrowUpRight className="h-3 w-3" />
              Promote
            </Button>
          )}
          {onToggleFullscreen && (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onToggleFullscreen}>
              {isFullscreen ? (
                <Minimize2 className="h-3.5 w-3.5" />
              ) : (
                <Maximize2 className="h-3.5 w-3.5" />
              )}
            </Button>
          )}
          {onClose && (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onClose}>
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Body */}
      {showPanes ? (
        <ResizablePanelGroup
          direction="horizontal"
          autoSaveId="cortex-agent-window"
          className="flex-1 min-h-0"
        >
          <ResizablePanel
            ref={railPanelRef}
            order={1}
            collapsible
            collapsedSize={0}
            defaultSize={16}
            minSize={12}
            maxSize={30}
            onCollapse={() => setRailCollapsed(true)}
            onExpand={() => setRailCollapsed(false)}
            className="min-w-0"
          >
            <SessionRail
              className="h-full"
              chats={engine.sessionLists.chats}
              work={engine.sessionLists.work}
              projects={projects}
              activeKind={engine.sessionKind}
              activeId={engine.sessionId}
              runs={engine.runs}
              onSelect={engine.selectSession}
              onNew={(k) => engine.startNewSession(k)}
            />
          </ResizablePanel>
          <ResizableHandle className="bg-border/20 hover:bg-primary/30 transition-colors" />
          <ResizablePanel order={2} minSize={30} defaultSize={58} className="min-w-0">
            {center}
          </ResizablePanel>
          <ResizableHandle className="bg-border/20 hover:bg-primary/30 transition-colors" />
          <ResizablePanel
            ref={workbenchPanelRef}
            order={3}
            collapsible
            collapsedSize={0}
            defaultSize={26}
            minSize={16}
            maxSize={45}
            onCollapse={() => setWorkbenchCollapsed(true)}
            onExpand={() => setWorkbenchCollapsed(false)}
            className="min-w-0"
          >
            <Workbench
              className="h-full"
              runs={engine.runs}
              orgId={currentOrganization?.id}
              projectName={currentProject?.name ?? undefined}
              modelName={engine.selectedModel}
              specialist={engine.selectedSpecialist}
              notebookId={engine.selectedNotebookId}
              onAnswer={(runId, opts) => void engine.resumeRun(runId, opts)}
            />
          </ResizablePanel>
        </ResizablePanelGroup>
      ) : (
        <div className="flex flex-1 min-h-0">{center}</div>
      )}
    </div>
  );
}
